/**
 * Shared "form.ts" builder for every real-code target — plan section 10's
 * "Shared TS IR factory: createForm | mdyForm | useMdyForm". Every Studio
 * validator kind maps to a @modyra/core validator function (not a
 * reimplementation), server validation uses the idiomatic `serverValidator()`
 * helper (matches usage, e.g. examples/angular's typed-form section),
 * and form-level validators compile through `compileExpressionToJs`
 * (ADR-0005: portable expression -> source). Only the schema-factory
 * import source and call name vary per target — that is `TargetProfile`.
 */
import { buildIndexes, } from "@modyra/studio-model";
import { compileExpressionToJs } from "./expression-compiler.js";
import { ImportResolver } from "./import-resolver.js";
import { printArray, printCall, printObject, printRegExp, printString } from "./ts-print.js";
function literalCode(value) {
    const json = JSON.stringify(value);
    return json === undefined ? "undefined" : json;
}
function collectRefs(expr, out) {
    const operands = expr.operands ?? (expr.operand !== undefined ? [expr.operand] : []);
    for (const o of operands) {
        if (o && typeof o === "object") {
            if ("nodeId" in o)
                out.push(o.nodeId);
            else if ("op" in o)
                collectRefs(o, out);
        }
    }
}
/**
 * The expression under which the validator is skipped, compiled against the field's own value.
 * `serverValidator()`'s `when` says when the validator *runs*, so the caller negates it.
 *
 * `when` receives the field's own value as its first argument, so this is only usable when `skipWhen`
 * refers to that same field, never a sibling (that would need the whole form value, a different
 * calling convention).
 */
function mapSkipWhen(expr, fieldId, fieldName, diagnostics) {
    const refs = [];
    collectRefs(expr, refs);
    if (refs.some((id) => id !== fieldId)) {
        diagnostics.push({
            code: "UNSUPPORTED_SKIP_WHEN",
            severity: "warning",
            message: `skipWhen on "${fieldName}" references another field; this target only supports self-referencing skipWhen, so it was omitted`,
            nodeId: fieldId,
        });
        return null;
    }
    return compileGuarded(expr, (id) => (id === fieldId ? "" : id), diagnostics, { nodeId: fieldId }, `skipWhen on "${fieldName}"`);
}
/**
 * Compiles an expression, answering a refusal with a diagnostic instead of an exception.
 *
 * The compiler throws on an operand it cannot print faithfully; a project file decides what it is
 * handed, so that refusal is a finding about the project and the caller omits what depended on it.
 */
function compileGuarded(expr, pathOf, diagnostics, where, what) {
    try {
        return compileExpressionToJs(expr, pathOf);
    }
    catch (error) {
        diagnostics.push({
            code: "INVALID_EXPRESSION",
            severity: "error",
            message: `${what} cannot be compiled and was omitted: ${error instanceof Error ? error.message : String(error)}`,
            ...where,
        });
        return null;
    }
}
function mapFieldValidator(v, node, imports, diagnostics, stubNameFor, validatorSource) {
    const msgArg = v.message !== undefined ? [printString(v.message)] : [];
    switch (v.kind) {
        case "required":
            imports.add(validatorSource, "required");
            return printCall("required", msgArg);
        case "email":
            imports.add(validatorSource, "email");
            return printCall("email", msgArg);
        case "min":
        case "max":
        case "minLength":
        case "maxLength": {
            // `Number.isFinite`, not `typeof`. NaN and both infinities have a number's type and survive a
            // type check, and `literalCode` is `JSON.stringify`, which turns each of them into `null` —
            // so `minLength: NaN` became `minLength(null)`, which accepts everything and declares
            // `minLength: null` as a fact the control then carries. An author writes a minimum, the
            // generated form has none, and nothing between the two says a word. A bound that is not a
            // finite number is no more usable than a bound that is a string, which was already reported.
            if (!Number.isFinite(v.value)) {
                diagnostics.push({ code: "MISSING_VALIDATOR_VALUE", severity: "warning", message: `Validator "${v.kind}" on "${node.name}" has no finite numeric value and was omitted`, nodeId: node.id, validatorId: v.id });
                return null;
            }
            imports.add(validatorSource, v.kind);
            return printCall(v.kind, [literalCode(v.value), ...msgArg]);
        }
        case "pattern": {
            if (typeof v.pattern !== "string") {
                diagnostics.push({ code: "MISSING_VALIDATOR_VALUE", severity: "warning", message: `Pattern validator on "${node.name}" has no pattern and was omitted`, nodeId: node.id, validatorId: v.id });
                return null;
            }
            imports.add(validatorSource, "pattern");
            return printCall("pattern", [printRegExp(v.pattern), ...msgArg]);
        }
        case "oneOf":
        case "eachOneOf": {
            if (!Array.isArray(v.value)) {
                diagnostics.push({ code: "MISSING_VALIDATOR_VALUE", severity: "warning", message: `Validator "${v.kind}" on "${node.name}" has no value list and was omitted`, nodeId: node.id, validatorId: v.id });
                return null;
            }
            imports.add(validatorSource, v.kind);
            return printCall(v.kind, [literalCode(v.value), ...msgArg]);
        }
        case "customRef": {
            const stubName = v.implementationRef ? stubNameFor.get(v.implementationRef) : undefined;
            if (!stubName) {
                diagnostics.push({ code: "MISSING_IMPLEMENTATION", severity: "error", message: `Custom validator on "${node.name}" has no resolvable implementation`, nodeId: node.id, validatorId: v.id });
                return null;
            }
            return stubName;
        }
        default:
            diagnostics.push({ code: "UNSUPPORTED_VALIDATOR", severity: "warning", message: `Validator kind "${v.kind}" is not supported by this target and was omitted`, nodeId: node.id, validatorId: v.id });
            return null;
    }
}
function mapArrayValidator(v, node, imports, diagnostics, validatorSource) {
    const msgArg = v.message !== undefined ? [printString(v.message)] : [];
    // The same distinction as the field bounds above: a row count of NaN is not a row count.
    if ((v.kind === "min" || v.kind === "max") && Number.isFinite(v.value)) {
        const fn = v.kind === "min" ? "minLength" : "maxLength";
        imports.add(validatorSource, fn);
        return printCall(fn, [literalCode(v.value), ...msgArg]);
    }
    diagnostics.push({ code: "UNSUPPORTED_VALIDATOR", severity: "warning", message: `Array validator kind "${v.kind}" is not supported by this target and was omitted`, nodeId: node.id, validatorId: v.id });
    return null;
}
/** The field()'s 3rd-arg `MdyFieldOptions` expression, built via the idiomatic `serverValidator()` helper (returns a ready-to-spread options fragment — never hand-built asyncValidators/asyncDebounceMs). */
function mapServerValidatorArg(node, idx, imports, diagnostics, stubNameFor, validatorSource) {
    const sv = node.serverValidator;
    if (!sv)
        return null;
    const stubName = stubNameFor.get(sv.implementationRef);
    if (!stubName) {
        diagnostics.push({ code: "MISSING_IMPLEMENTATION", severity: "error", message: `Server validator on "${node.name}" has no resolvable implementation`, nodeId: node.id, validatorId: sv.id });
        return null;
    }
    imports.add(validatorSource, "serverValidator");
    const optionProps = [];
    if (sv.debounceMs !== undefined)
        optionProps.push({ key: "debounceMs", value: literalCode(sv.debounceMs) });
    if (sv.dependencies.length) {
        // dependsOn wants dotted form-value paths, never internal Studio node IDs.
        optionProps.push({ key: "dependsOn", value: literalCode(sv.dependencies.map((d) => idx.pathByNode.get(d.nodeId) ?? d.nodeId)) });
    }
    if (sv.timeoutMs !== undefined)
        optionProps.push({ key: "timeoutMs", value: literalCode(sv.timeoutMs) });
    if (sv.skipWhen) {
        const when = mapSkipWhen(sv.skipWhen, node.id, node.name, diagnostics);
        if (when)
            optionProps.push({ key: "when", value: `(value) => !(${when})` });
    }
    return printCall("serverValidator", optionProps.length ? [stubName, printObject(optionProps)] : [stubName]);
}
function mapField(node, idx, imports, diagnostics, stubNameFor, profile, validatorSource) {
    imports.add(profile.factoryImportSource, "field");
    const validatorCodes = node.validators
        .map((v) => mapFieldValidator(v, node, imports, diagnostics, stubNameFor, validatorSource))
        .filter((c) => c !== null);
    const serverValidatorArg = mapServerValidatorArg(node, idx, imports, diagnostics, stubNameFor, validatorSource);
    const args = [literalCode(node.initialValue)];
    if (validatorCodes.length || serverValidatorArg)
        args.push(printArray(validatorCodes));
    if (serverValidatorArg)
        args.push(serverValidatorArg);
    return printCall("field", args);
}
function mapGroup(node, idx, imports, diagnostics, stubNameFor, profile, validatorSource) {
    imports.add(profile.factoryImportSource, "group");
    const props = node.children.map((child) => ({ key: child.name, value: mapNode(child, idx, imports, diagnostics, stubNameFor, profile, validatorSource) }));
    return printCall("group", [printObject(props)]);
}
/** A collection of either kind: the factory is the node's own name, and a row is a whole node. */
function mapCollection(node, idx, imports, diagnostics, stubNameFor, profile, validatorSource) {
    imports.add(profile.factoryImportSource, node.node);
    const itemCode = mapNode(node.item, idx, imports, diagnostics, stubNameFor, profile, validatorSource);
    const validatorCodes = node.validators
        .map((v) => mapArrayValidator(v, node, imports, diagnostics, validatorSource))
        .filter((c) => c !== null);
    const optionProps = [{ key: "initial", value: literalCode(node.initialRows) }];
    if (validatorCodes.length)
        optionProps.push({ key: "validators", value: printArray(validatorCodes) });
    return printCall(node.node, [itemCode, printObject(optionProps)]);
}
function mapNode(node, idx, imports, diagnostics, stubNameFor, profile, validatorSource) {
    if (node.node === "field")
        return mapField(node, idx, imports, diagnostics, stubNameFor, profile, validatorSource);
    if (node.node === "group")
        return mapGroup(node, idx, imports, diagnostics, stubNameFor, profile, validatorSource);
    return mapCollection(node, idx, imports, diagnostics, stubNameFor, profile, validatorSource);
}
function mapFormValidator(fv, idx, diagnostics) {
    const pathOf = (id) => idx.pathByNode.get(id) ?? id;
    const condition = compileGuarded(fv.condition, pathOf, diagnostics, { validatorId: fv.id }, `Form validator "${fv.id}"`);
    if (condition === null)
        return null;
    const targetRefs = fv.errorTarget ? [fv.errorTarget] : fv.dependencies;
    const paths = targetRefs.map((r) => pathOf(r.nodeId)).filter((p) => p !== "");
    return `crossField(${literalCode(paths)}, (value) => (${condition}) ? null : ${literalCode(fv.message)})`;
}
/** Builds `form.ts` for the given target `profile`. `stubNameFor` maps implementationRef id -> the generated stub function name. */
export function buildFormModule(project, stubNameFor, profile) {
    const diagnostics = [];
    const imports = new ImportResolver();
    // The module's own top-level declarations: an import of either name would be a duplicate binding.
    imports.declare("schema", profile.hookExportName ?? "form");
    if (project.schema.node !== "group") {
        diagnostics.push({ code: "INVALID_ROOT", severity: "error", message: "Project schema root must be a group", nodeId: project.schema.id });
        return { code: "", diagnostics };
    }
    // The profile is a caller's, and `TargetRegistry` is public — a custom target supplying one is the
    // case this exists for. Without a factory source every import became `from "undefined"`: a module
    // that cannot compile, emitted with no diagnostic, and the target's own author is the only person
    // who could act on it.
    if (typeof profile.factoryImportSource !== "string" || profile.factoryImportSource.length === 0) {
        diagnostics.push({
            code: "INVALID_TARGET_PROFILE",
            severity: "error",
            message: "Target profile has no factoryImportSource, so the generated module would import from nowhere",
        });
        return { code: "", diagnostics };
    }
    const validatorSource = profile.validatorsImportSource ?? "@modyra/core";
    const idx = buildIndexes(project);
    const schemaProps = project.schema.children.map((child) => ({
        key: child.name,
        value: mapNode(child, idx, imports, diagnostics, stubNameFor, profile, validatorSource),
    }));
    const schemaCode = printObject(schemaProps);
    const formOptionProps = [];
    const mapped = project.formValidators.map((fv) => mapFormValidator(fv, idx, diagnostics)).filter((c) => c !== null);
    if (mapped.length) {
        imports.add(validatorSource, "crossField");
        formOptionProps.push({ key: "validators", value: printArray(mapped) });
    }
    if (project.behaviors.draft) {
        const draft = project.behaviors.draft;
        const draftProps = [{ key: "key", value: literalCode(draft.key) }];
        if (draft.exclude?.length) {
            draftProps.push({ key: "exclude", value: literalCode(draft.exclude.map((r) => idx.pathByNode.get(r.nodeId) ?? r.nodeId)) });
        }
        formOptionProps.push({ key: "draft", value: printObject(draftProps) });
    }
    formOptionProps.push({ key: "history", value: "true" });
    imports.add(profile.factoryImportSource, profile.createCallName);
    // Only stub names actually referenced in the emitted schema/options code get imported —
    // a stub declared in the project but never wired to a validator/behavior stays unused here
    // (the "no unused imports" gate applies to stub imports too, not just factory ones).
    const emitted = schemaCode + "\n" + formOptionProps.map((p) => p.value).join("\n");
    const referencedNames = new Set([...emitted.matchAll(/\b[A-Za-z_$][A-Za-z0-9_$]*\b/g)].map((m) => m[0]));
    const referencedStubs = [...new Set(stubNameFor.values())].filter((name) => referencedNames.has(name));
    if (referencedStubs.length)
        imports.add("./stubs.js", ...referencedStubs);
    const schemaArg = profile.wrapSchemaInThunk ? "() => schema" : "schema";
    const call = `${profile.createCallName}(${schemaArg}, ${printObject(formOptionProps)})`;
    const exportStatement = profile.hookExportName
        ? `export function ${profile.hookExportName}() {\n  return ${call.split("\n").join("\n  ")};\n}\n`
        : `export const form = ${call};\n`;
    // What the resolver refused, surfaced where every other target-profile finding is: a name it could
    // not import is a module that would not have compiled, and the person who can fix it is the one
    // who wrote the profile.
    for (const problem of imports.problems) {
        diagnostics.push({ code: "INVALID_TARGET_PROFILE", severity: "error", message: problem });
    }
    if (imports.problems.length > 0)
        return { code: "", diagnostics };
    const code = `${imports.print()}\n\nconst schema = ${schemaCode};\n\n${exportStatement}`;
    return { code, diagnostics };
}
